import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { toastr } from 'react-redux-toastr';
import { ssgFormsAdapters } from '../common';
import * as surveyActions from './survey.actions';
import { Title, Header } from '../layout';
import { ConfirmModalWindow, SsgFormComponent, LoadingPane, ImageDisplay, accessibilityNotificationsEmitter, ssgFormActions } from '@social-solutions/ssg-forms';
import queryString from 'query-string';
import './survey.scss';

/** specific errors that indicate that a survey token is no longer valid */
export const surveyErrors = {
  /** the survey expired */
  expired: "assignment-expired",
  /** an admin manually closed the survey */
  closed: "assignment-closed",
  /** the assignment has already been completed by the participant */
  completed: "assignment-complete",
}

/** displays a survey form for a participant to fill out, only valid to fill out once */
export class SurveyComponent extends Component {
  constructor() {
    super();
    this.state = {
      localState: null, // hold the state needed to call save
      localModal: null  // hold a reference to the confirmation modal
    }
  }

  componentDidMount() {
    const { surveyActions, location } = this.props;
    const { search } = location || {};
    const query = queryString.parse(search);
    const token = query.tokenId;

    surveyActions.fetchSurveyForm(token);
  }

  componentWillUpdate(nextProps, nextState) {
    const { surveyActions, location : oldLocation } = this.props;
    const { search : oldSearch } = oldLocation || {};
    const oldQuery = queryString.parse(oldSearch);
    const oldToken = oldQuery.tokenId;

    const { location } = nextProps;
    const { search } = location || {};
    const query = queryString.parse(search);
    const token = query.tokenId;

    if(token && token !== oldToken) {
      surveyActions.fetchSurveyForm(token);
    }
  }

  componentWillUnmount() {
    const { ssgFormActions, location } = this.props;
    const { search } = location || {};
    const query = queryString.parse(search);
    const token = query.tokenId;


    ssgFormActions.hardReset('survey-' + token);
  }

  /**
   * Prompt the user for confirmation of form data submission, as
   * long as the data has passed validation.
   * @param {*} state State to use if the user confirms submission via the modal.
   */
  handleConfirmPopupWindowDisplay(state) {
    if(!!state && !!state.validationResult && state.validationResult.passedValidation) {
      this.setState({localState : state});
      this.state.localModal.show();
    }
    else {
      window.scrollTo(0, 0);
    }
  }

  /**
   * Save the data, or return to the form data entry, based on the user's choice
   * in the confirm modal.
   * @param {*} choice True if the user picked yes on the modal, false otherwise
   * @param {*} state State to use for saving, if the user selected yes.
   */
  handleConfirmPopupWindowChoice(choice, state) {
    if (choice) {
      this.handleSave(state);
    }
    else {
      this.setState({localState : null});
    }
  }

  handleResetForm() {
    accessibilityNotificationsEmitter.assertive("Cleared responses to the assigned form.");
  }

  /**
   * Submits the form
   * @param {*} state
   */
  handleSave(state) {
    const { surveyActions, location } = this.props;
    const { search } = location || {};
    const query = queryString.parse(search);
    const token = query.tokenId;
    if(!!state && !!state.validationResult && state.validationResult.passedValidation) {
      surveyActions.submitSurveyForm(token, state.value)
        .then(response => {
          surveyActions.fetchSurveyForm(token);
        })
        .catch(err => {
          if(err.errorCode === surveyErrors.expired ||
            err.errorCode === surveyErrors.closed ||
            err.errorCode === surveyErrors.completed) {
            surveyActions.fetchSurveyForm(token);
          }
          else {
            toastr.error(err.errorCode, err.errorMessage);
          }
        });
    }
  }



  renderExpiredError() {
    return (
      <div className="centered-content">
        <ImageDisplay
          src="/form_expired.png"
          primaryText={"Form Expired"}
          secondaryText={"This form is no longer available."}/>
      </div>
    );
  }

  renderCompletedDisplay() {
    return (
      <div className="centered-content">
        <ImageDisplay
          src="/all_done.png"
          primaryText={"All Done!"}
          secondaryText={"Nice work! You have completed your assigned form."}/>
      </div>
    );
  }

  renderSurvey() {
    const { survey, ssgForms, location } = this.props;
    const { search } = location || {};
    const query = queryString.parse(search);
    const token = query.tokenId;
    const { surveyForms, formSubmission } = survey || {};

    const surveyFormState = surveyForms && surveyForms.get(token);
    const { data : surveyForm } = surveyFormState || {};
    const fieldFormatSetting = !!surveyForm && surveyForm.fieldFormatSetting;

    const submitAnswersState = formSubmission && formSubmission.get(token);
    const { isFetching : isSubmitting } = submitAnswersState || {};

    const { processedForms } = ssgForms || {};
    const formState = processedForms && processedForms.get('survey-' + token);
    const { value } = formState || { value: null }
    var hasValues = !!value && !value.isEmpty();

    const confirmModalProps = {
      confirmTitle: 'Submit Form',
      confirmMsg: 'Are you ready to submit this form?'
    }

    const formProps = {
      id: 'survey-' + token,
      form: surveyForm,
      saving: isSubmitting,
      buttonLayout: [
        {
          id: 'reset',
          display: 'Reset',
          ariaLabel: 'Reset Form',
          buttonType: 'default',
          functionType: 'reset',
          onClick: (state) => this.handleResetForm(),
        },
        {
          id: 'submit',
          display: 'Submit',
          ariaLabel: 'Submit Form',
          type: 'submit',
          buttonType: 'primary',
          functionType: 'submit',
          spinnerOn: isSubmitting,
          disabled: !hasValues,
          onClick: (state) => this.handleConfirmPopupWindowDisplay(state)
        }
      ],
      showRequiredKey: true,
      css: {
        form: "ssg-form survey-form-display",
        formBody: 'ssg-forms-body',
        actions: "ssg-forms-actions survey-form-actions-display",
      },
      country: fieldFormatSetting,
    };

    var surveyLoadingState = ssgFormsAdapters.convertLoadingState(surveyFormState);

    return (
      <LoadingPane id="form" loadingState={surveyLoadingState}>
        <ConfirmModalWindow id="survey-form-confirm"
          title={confirmModalProps.confirmTitle}
          modalRef={(modal) => this.setState( {localModal : modal})}
          onClose={(e, confirmed) => this.handleConfirmPopupWindowChoice(confirmed, this.state.localState)}>
          {confirmModalProps.confirmMsg}
        </ConfirmModalWindow>
        <SsgFormComponent {...formProps}/>
      </LoadingPane>
    );
  }

  render() {

    const { survey, location } = this.props;
    const { search } = location || {};
    const query = queryString.parse(search);
    const token = query.tokenId;
    const { surveyForms } = survey || {};

    const surveyFormState = surveyForms && surveyForms.get(token);
    const { err: validationError } = surveyFormState || {};

    var display = null;
    if(!!validationError) {
      if(validationError.errorCode === surveyErrors.expired || validationError.errorCode === surveyErrors.closed) {
        display = this.renderExpiredError();
      }
      else if(validationError.errorCode === surveyErrors.completed) {
        display = this.renderCompletedDisplay();
      }
      else {
        display = this.renderSurvey();
      }
    }
    else {
      display = this.renderSurvey();
    }

    return (
      <div>
        <Header />
        <Title title="Survey Form"/>
        <div id="view" className="survey-view">
           {display}
        </div>
      </div>
    );
  }
}

SurveyComponent.propTypes = {
  surveyActions: PropTypes.object.isRequired,
  survey: PropTypes.object.isRequired,
  ssgForms: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      tokenId: PropTypes.isRequired,
    })
  })
};

const mapStateToProps = ({ survey, ssgForms }) => {
  return {
    survey,
    ssgForms,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    surveyActions: bindActionCreators(surveyActions, dispatch),
    ssgFormActions: bindActionCreators(ssgFormActions, dispatch),
    redirectTo: bindActionCreators(push, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SurveyComponent);



// WEBPACK FOOTER //
// src/survey/survey.component.js